/**
 * Prompt → documentSemanticSearch → stitched HLS via Brightcove web player (Video.js).
 * Sidebar shows clip list with active highlight, jump-to-clip, continue-watching, and details.
 */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import { List, Map as ImMap } from 'immutable'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { useApolloClient } from '@apollo/client'
import { useSelector } from 'react-redux'
import compose from 'recompose/compose'
import get from 'lodash/get'
import findIndex from 'lodash/findIndex'
import debounce from 'lodash/debounce'
import ReactPlayerLoader from '@brightcove/react-player-loader'
import { get as getConfig } from '../../config/index'
import { getAuthJwt } from '../../services/auth/index'
import { DOCUMENT_SEMANTIC_SEARCH, GET_CONTENT_SEARCH_OPTIONS } from '../../services/semantic-stitch/query'
import {
  CURATION_MODE_PRESETS,
  DEFAULT_CURATION_MODE,
  CURATION_MODE_LABEL_KEY,
  CURATION_MODE_DESC_KEY,
  MOSAIC_SEARCH_INCLUDE_SUMMARY,
  MOSAIC_SEARCH_INCLUDE_TRANSITIONS,
} from '../../services/semantic-stitch/constants'
import {
  FILTER_SCOPE_ANY,
  FILTER_SCOPE_CENTER,
  FILTER_SCOPE_SERIES,
  FILTER_SCOPE_TOPIC,
  FILTER_SCOPE_VIDEO,
  FILTER_SCOPE_LABEL_KEYS,
  PUBLISHED_AGE_OPTIONS,
  MAX_SEASONS,
  TYPEAHEAD_DEBOUNCE_MS,
  TYPEAHEAD_LIMIT,
  buildSearchFilter,
  buildTypeaheadWhere,
  hasActiveFilterState,
  isFilterScopeIncomplete,
} from '../../services/semantic-stitch/filters'
import { getPrimary } from '../../services/languages/index'
import { EN } from '../../services/languages/constants'
import { DocumentSemanticSearchLanguage } from '../../graphql-types'
import {
  getVideoOpts,
  MIME_TYPE_VIDEO_PLAYER,
} from '../../video-js/index'
import {
  VIDEO_PLAYER_INACTIVITY_TIMEOUT,
  MOBILE_VIDEO_PLAYER_INACTIVITY_TIMEOUT,
} from '../../services/video/index'
import { isIOS } from '../../environment/index'
import {
  CONTENT_IMAGE_TYPE_KEYART_4X3_WITH_TEXT,
  CONTENT_IMAGE_TYPE_KEYART_WITH_TEXT,
} from '../../services/content/index'
import marketplaceProductUrlThroughShop from '../../services/url/marketplace'
import ArrowRight from '../Icon/icons/ArrowRight/index'
import GaiaLeaf from '../Icon/icons/GaiaLeaf/index'
import Metaphysics from '../Icon/icons/Metaphysics/index'
import PlayPlaylistOutline from '../Icon/icons/PlayPlaylistOutline/index'
import Reply from '../Icon/icons/Reply/index'
import { connect as connectStaticText } from '../StaticText/connect'
import { H3 } from '../Heading/index'
import Link, { TARGET_BLANK } from '../Link/index'
import {
  registerStitchedPlayerPlugins,
  configureStitchedPlayerPlugins,
} from './stitched-player-plugins'
import MosaicLoadingSplash from './MosaicLoadingSplash'

/**
 * Parse a timecode that may be plain seconds ("45.2"), MM:SS.mmm ("02:30.000"),
 * or HH:MM:SS.mmm ("01:02:03.500") into a floating-point second count.
 * The API returns HH:MM:SS.mmm format from formatSecondsToTimecode in span-map.js.
 */
const CONTINUE_LEAD_IN = 5

function timecodeToSeconds (tc) {
  if (!tc) return 0
  const parts = String(tc).split(':')
  if (parts.length === 1) return parseFloat(parts[0]) || 0
  if (parts.length === 2) return parseInt(parts[0], 10) * 60 + parseFloat(parts[1])
  return parseInt(parts[0], 10) * 3600 + parseInt(parts[1], 10) * 60 + parseFloat(parts[2])
}

function formatSeconds (totalSeconds) {
  const s = Math.round(totalSeconds || 0)
  const totalM = Math.floor(s / 60)
  const h = Math.floor(totalM / 60)
  const m = totalM % 60
  const sec = String(s % 60).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`
}

function buildClipOffsets (spans) {
  let acc = 0
  return spans.map((span) => {
    const startSec = timecodeToSeconds(span.startTimecode)
    const endSec = timecodeToSeconds(span.endTimecode)
    const clipDuration = endSec - startSec
    const stitchedStart = acc
    acc += clipDuration
    return {
      ...span, startSec, endSec, stitchedStart, clipDuration,
    }
  })
}

/**
 * Split `text` around `highlight`, returning an array of React nodes with the
 * matching portion wrapped in a `<mark>` element. Falls back to plain text when
 * `highlight` is absent or not found.
 * @param {string} text
 * @param {string|null|undefined} highlight
 * @returns {React.ReactNode[]}
 */
function renderWithHighlight (text, highlight) {
  if (!highlight) {
    return [text]
  }
  const idx = text.indexOf(highlight)
  if (idx < 0) {
    return [text]
  }
  const parts = []
  if (idx > 0) {
    parts.push(text.slice(0, idx))
  }
  parts.push(<mark key="match">{highlight}</mark>)
  if (idx + highlight.length < text.length) {
    parts.push(text.slice(idx + highlight.length))
  }
  return parts
}

function ClipCard ({
  clip,
  index,
  isActive,
  isPast,
  currentTime,
  playerRef,
  isCompact,
  staticText,
}) {
  const cardRef = useRef(null)
  const [showDetails, setShowDetails] = useState(false)
  const [showTranscript, setShowTranscript] = useState(false)

  const t = (key) => staticText.getIn(['data', key], '')
  const title = get(clip, 'content.title') || t('clipLabel').replace('{n}', String(index + 1))
  const shortDesc = get(clip, 'content.shortDescription') || ''
  const path = get(clip, 'content.path') || ''
  const thumbnailUrl = get(clip, ['content', 'imageContentRelationships', 0, 'image', 'url']) || ''
  const seriesTitle = get(clip, ['content', 'contentSeries', 0, 'title']) || ''
  const seasonNum = get(clip, 'content.seasonNumber')
  const episodeNum = get(clip, 'content.episodeNumber')
  const seasonEpisodeStr = [
    seasonNum != null && `S${seasonNum}`,
    episodeNum != null && `E${String(episodeNum).padStart(2, '0')}`,
  ].filter(Boolean).join(' · ')
  const clipNumLabel = t('clipLabel').replace('{n}', String(index + 1).padStart(2, '0'))

  let progressPct = 0
  if (isActive && clip.clipDuration > 0) {
    const elapsed = (currentTime - clip.stitchedStart) / clip.clipDuration
    progressPct = Math.min(100, Math.max(0, elapsed * 100))
  } else if (isPast) {
    progressPct = 100
  }

  let rawPosition
  if (isActive) {
    rawPosition = Math.floor(clip.startSec + (currentTime - clip.stitchedStart)) - CONTINUE_LEAD_IN
  } else if (isPast) {
    rawPosition = Math.floor(clip.endSec - CONTINUE_LEAD_IN)
  } else {
    rawPosition = Math.floor(clip.startSec - CONTINUE_LEAD_IN)
  }
  const continuePosition = Math.max(0, rawPosition)

  const continueHref = path
    ? `/${path}?startAt=${continuePosition}&fullplayer=feature`
    : null

  useEffect(() => {
    if (isActive && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [isActive])

  const handleJump = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.currentTime(clip.stitchedStart)
    }
  }, [playerRef, clip.stitchedStart])

  const handleContinue = useCallback(() => {
    // Pause the stitched player before opening the source video in a new window
    // so both tabs don't play audio simultaneously.
    if (playerRef.current) {
      playerRef.current.pause()
    }
  }, [playerRef])

  const cardCls = [
    'semantic-stitch-page__clip-card',
    isActive && 'semantic-stitch-page__clip-card--active',
    isCompact && 'semantic-stitch-page__clip-card--compact',
  ].filter(Boolean).join(' ')

  if (isCompact) {
    return (
      <div
        ref={cardRef}
        className={cardCls}
        onClick={handleJump}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleJump()}
        title={title}
      >
        <span className="semantic-stitch-page__clip-num">{clipNumLabel}</span>
        {thumbnailUrl && (
          <img
            className="semantic-stitch-page__clip-thumb--compact"
            src={thumbnailUrl}
            alt=""
          />
        )}
        <div className="semantic-stitch-page__clip-progress">
          <div
            className="semantic-stitch-page__clip-progress-fill"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>
    )
  }

  return (
    <div ref={cardRef} className={cardCls}>
      <div
        className="semantic-stitch-page__clip-header"
        onClick={handleJump}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleJump()}
      >
        {thumbnailUrl && (
          <img
            className="semantic-stitch-page__clip-thumb"
            src={thumbnailUrl}
            alt=""
          />
        )}
        <div className="semantic-stitch-page__clip-meta">
          <span className="semantic-stitch-page__clip-num">{clipNumLabel}</span>
          <span className="semantic-stitch-page__clip-title">{title}</span>
          {(seriesTitle || seasonEpisodeStr) && (
            <span className="semantic-stitch-page__clip-series-info">
              {seriesTitle && <span className="semantic-stitch-page__clip-series-title">{seriesTitle}</span>}
              {seasonEpisodeStr && <span className="semantic-stitch-page__clip-season-episode">{seasonEpisodeStr}</span>}
            </span>
          )}
          <span className="semantic-stitch-page__clip-times">
            {formatSeconds(clip.startSec)}{' – '}{formatSeconds(clip.endSec)}
          </span>
        </div>
      </div>
      <div className="semantic-stitch-page__clip-progress">
        <div
          className="semantic-stitch-page__clip-progress-fill"
          style={{ width: `${progressPct}%` }}
        />
      </div>
      <div className="semantic-stitch-page__clip-actions">
        {continueHref && (
          <a
            className="semantic-stitch-page__clip-btn semantic-stitch-page__clip-btn--continue"
            href={continueHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleContinue}
          >
            {t('continueWatching')}
          </a>
        )}
        {shortDesc && (
          <button
            type="button"
            className="semantic-stitch-page__clip-btn"
            onClick={() => setShowDetails((v) => !v)}
          >
            {showDetails ? t('hideDetails') : t('details')}
          </button>
        )}
        {clip.transcriptText && (
          <button
            type="button"
            className="semantic-stitch-page__clip-btn"
            onClick={() => setShowTranscript((v) => !v)}
          >
            {showTranscript ? t('hideTranscript') : t('transcript')}
          </button>
        )}
      </div>
      {showDetails && shortDesc && (
        <p className="semantic-stitch-page__clip-desc">{shortDesc}</p>
      )}
      {showTranscript && clip.transcriptText && (
        <p className="semantic-stitch-page__clip-transcript">
          {renderWithHighlight(clip.transcriptText, clip.matchedText)}
        </p>
      )}
    </div>
  )
}

ClipCard.propTypes = {
  clip: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  isActive: PropTypes.bool.isRequired,
  isPast: PropTypes.bool.isRequired,
  currentTime: PropTypes.number.isRequired,
  playerRef: PropTypes.object.isRequired,
  isCompact: PropTypes.bool.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
}

function clipListSummary (staticText, count) {
  if (count === 1) {
    return staticText.getIn(['data', 'oneClip'], '1 clip')
  }
  return String(staticText.getIn(['data', 'manyClips'], '{count} clips'))
    .replace(/\{count\}/gu, String(count))
}

function TransitionCard ({ clip, isActive, staticText }) {
  const t = (key) => staticText.getIn(['data', key], '')
  const title = get(clip, 'content.title') || null

  const cardCls = [
    'semantic-stitch-page__transition-card',
    isActive && 'semantic-stitch-page__transition-card--active',
  ].filter(Boolean).join(' ')

  return (
    <div className={cardCls}>
      <span className="semantic-stitch-page__transition-badge">{t('transitionCardLabel')}</span>
      {title && <span className="semantic-stitch-page__transition-title">{title}</span>}
    </div>
  )
}

TransitionCard.propTypes = {
  clip: PropTypes.object.isRequired,
  isActive: PropTypes.bool.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
}

function ClipSidebar ({
  clips,
  contentClips,
  activeIndex,
  showTransitions,
  hasTransitions,
  onToggleShowTransitions,
  currentTime,
  playerRef,
  isCompact,
  onToggleCompact,
  staticText,
}) {
  const t = (key) => staticText.getIn(['data', key], '')

  // When showTransitions is false, display only content clips and map active index
  // relative to contentClips. When a transition is playing, no content clip is highlighted.
  const displayClips = showTransitions ? clips : contentClips
  const displayActiveIndex = useMemo(() => {
    if (showTransitions) return activeIndex
    if (activeIndex < 0) return -1
    const activeSpan = clips[activeIndex]
    if (!activeSpan || activeSpan.kind === 'TRANSITION') return -1
    return contentClips.findIndex(
      (c) => c.contentId === activeSpan.contentId && c.startTimecode === activeSpan.startTimecode,
    )
  }, [showTransitions, activeIndex, clips, contentClips])

  // Track content-clip sequential number for display when showing mixed list with transitions.
  let contentClipNum = 0

  return (
    <div className={`semantic-stitch-page__sidebar${isCompact ? ' semantic-stitch-page__sidebar--compact' : ''}`}>
      <div className="semantic-stitch-page__sidebar-header">
        {!isCompact && (
          <>
            <span className="semantic-stitch-page__sidebar-title">
              {clipListSummary(staticText, contentClips.length)}
            </span>
            {hasTransitions && (
              <button
                type="button"
                className="semantic-stitch-page__sidebar-transitions-toggle"
                onClick={onToggleShowTransitions}
              >
                {t(showTransitions ? 'hideTransitions' : 'showTransitions')}
              </button>
            )}
          </>
        )}
        <button
          type="button"
          className="semantic-stitch-page__sidebar-toggle"
          onClick={onToggleCompact}
          title={isCompact
            ? staticText.getIn(['data', 'expandSidebar'], '')
            : staticText.getIn(['data', 'collapseSidebar'], '')}
        >
          {isCompact ? '»' : '«'}
        </button>
      </div>
      <div className="semantic-stitch-page__clip-list">
        {displayClips.map((clip, i) => {
          if (clip.kind === 'TRANSITION') {
            return (
              <TransitionCard
                key={`transition-${i}-${clip.contentId}`}
                clip={clip}
                isActive={i === displayActiveIndex}
                staticText={staticText}
              />
            )
          }
          const clipIndex = contentClipNum
          contentClipNum += 1
          return (
            <ClipCard
              key={`${clip.contentId}-${clip.startTimecode}`}
              clip={clip}
              index={clipIndex}
              isActive={i === displayActiveIndex}
              isPast={i < displayActiveIndex}
              currentTime={currentTime}
              playerRef={playerRef}
              isCompact={isCompact}
              staticText={staticText}
            />
          )
        })}
      </div>
    </div>
  )
}

ClipSidebar.propTypes = {
  clips: PropTypes.array.isRequired,
  contentClips: PropTypes.array.isRequired,
  activeIndex: PropTypes.number.isRequired,
  showTransitions: PropTypes.bool.isRequired,
  hasTransitions: PropTypes.bool.isRequired,
  onToggleShowTransitions: PropTypes.func.isRequired,
  currentTime: PropTypes.number.isRequired,
  playerRef: PropTypes.object.isRequired,
  isCompact: PropTypes.bool.isRequired,
  onToggleCompact: PropTypes.func.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
}

function StitchedManifestPlayer ({
  manifestUrl,
  subtitlesUrl,
  language,
  onPlayerReady,
  onPlayerError,
  user,
  staticText,
  touchable,
}) {
  const { account, player } = get(getConfig(), 'brightcove', {})
  const vjsOpts = getVideoOpts({
    user,
    controls: true,
    loop: false,
    isLive: false,
    playsinline: isIOS(),
    inactivityTimeout: touchable
      ? MOBILE_VIDEO_PLAYER_INACTIVITY_TIMEOUT
      : VIDEO_PLAYER_INACTIVITY_TIMEOUT,
  })

  const onSuccess = useCallback(({ ref: dataPlayer }) => {
    registerStitchedPlayerPlugins()
      .then(() => {
        dataPlayer.ready(() => {
          dataPlayer.src({ type: MIME_TYPE_VIDEO_PLAYER, src: manifestUrl })
          if (subtitlesUrl) {
            try {
              dataPlayer.addRemoteTextTrack({
                src: subtitlesUrl,
                kind: 'subtitles',
                label: 'Transcript',
                srclang: (language || 'en').slice(0, 2).toLowerCase(),
              }, false)
            } catch (e) {
              // Subtitle attachment is best-effort; player still works without it.
            }
          }
          try {
            configureStitchedPlayerPlugins(dataPlayer, staticText)
          } catch (e) {
            // Control plugins are best-effort; core playback still works.
          }
          onPlayerReady(dataPlayer)
        })
      })
      .catch((err) => {
        onPlayerError(err instanceof Error ? err : new Error(String(err)))
      })
  }, [manifestUrl, subtitlesUrl, language, onPlayerReady, onPlayerError, staticText])

  if (!account || !player) {
    return (
      <p className="semantic-stitch-page__player-error">
        {staticText.getIn(['data', 'brightcoveNotConfigured'], '')}
      </p>
    )
  }
  return (
    <div className="semantic-stitch-page__player-frame video-player">
      <ReactPlayerLoader
        key={manifestUrl}
        accountId={account}
        playerId={player}
        onSuccess={onSuccess}
        onFailure={onPlayerError}
        options={vjsOpts}
      />
    </div>
  )
}

StitchedManifestPlayer.propTypes = {
  manifestUrl: PropTypes.string.isRequired,
  subtitlesUrl: PropTypes.string,
  language: PropTypes.string,
  onPlayerReady: PropTypes.func.isRequired,
  onPlayerError: PropTypes.func.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  touchable: PropTypes.bool.isRequired,
}

StitchedManifestPlayer.defaultProps = {
  subtitlesUrl: null,
  language: 'en',
}

/**
 * When manifestUrl is a master playlist, find the media playlist by scanning lines after
 * #EXT-X-STREAM-INF (HLS: URI on the next line).
 */
function resolveStreamInfMediaUri (text) {
  if (!text.includes('#EXT-X-STREAM-INF')) return null
  const lines = text.split(/\r?\n/u)
  const i = findIndex(
    lines,
    (line, idx) => (
      idx < lines.length - 1
        && line.trim().startsWith('#EXT-X-STREAM-INF')
    ),
  )
  if (i < 0) return null
  const next = lines[i + 1].trim()
  if (next && !next.startsWith('#')) {
    return next
  }
  return null
}

/**
 * Normalizes a content `path` (which may already include a leading `/`) to a leading-slash form.
 */
function normalizeContentPath (path) {
  if (!path) {
    return null
  }
  return path.startsWith('/') ? path : `/${path}`
}

/**
 * Maps the site's primary language code (user preference, same as `getPrimary` elsewhere)
 * to `DocumentSemanticSearchInput.language`. Unsupported codes fall back to English search.
 * @param {string|undefined} primary
 */
function primaryLanguageToSemanticSearchLanguage (primary) {
  const code = String(primary || EN).toLowerCase().split('-')[0]
  if (code === 'es') return DocumentSemanticSearchLanguage.Es
  if (code === 'de') return DocumentSemanticSearchLanguage.De
  if (code === 'fr') return DocumentSemanticSearchLanguage.Fr
  return DocumentSemanticSearchLanguage.En
}

/**
 * Resolves a Content object from `data.products` / `data.articles` to a target href.
 * Products use `ctaProductUrl` (same `/shop?redirect=` gateway as video-detail marketplace tiles);
 * falls back to wrapping `ctaPath` when needed. Articles use relative `path`.
 */
function buildContentSectionHref (item, kind) {
  if (kind === 'product') {
    if (item.ctaProductUrl) {
      return item.ctaProductUrl
    }
    return marketplaceProductUrlThroughShop(item.ctaPath)
  }
  return normalizeContentPath(item.path)
}

/**
 * Marketplace mosaic: prefers 4×3 key art when present; falls back to 16×9.
 * @param {{ imageContentRelationships?: Array<{ type?: string, image?: { url?: string } }> }} item
 */
function mosaicProductThumbnailUrl (item) {
  const rels = item.imageContentRelationships
  if (!rels || rels.length === 0) {
    return ''
  }
  const matchType = (t) => (r) => r.type && r.type.toLowerCase() === t.toLowerCase()
  const fourThree = rels.find(matchType(CONTENT_IMAGE_TYPE_KEYART_4X3_WITH_TEXT))
  if (fourThree?.image?.url) {
    return fourThree.image.url
  }
  const sixteenNine = rels.find(matchType(CONTENT_IMAGE_TYPE_KEYART_WITH_TEXT))
  if (sixteenNine?.image?.url) {
    return sixteenNine.image.url
  }
  return ''
}

let mosaicArticleThumbSrcCache = null

/**
 * Stock Gaia Article artwork for mosaic cards (articles have no per-item imagery in search).
 */
function MosaicArticleStockThumbnail () {
  const [src, setSrc] = useState(mosaicArticleThumbSrcCache)
  useEffect(() => {
    if (mosaicArticleThumbSrcCache) {
      setSrc(mosaicArticleThumbSrcCache)
      return undefined
    }
    let cancelled = false
    import('./img/gaia-article-thumb.png').then((mod) => {
      if (cancelled) return
      mosaicArticleThumbSrcCache = mod.default
      setSrc(mosaicArticleThumbSrcCache)
    })
    return () => {
      cancelled = true
    }
  }, [])
  if (!src) {
    return <div className="semantic-stitch-page__mosaic-card-thumb--placeholder" />
  }
  return <img src={src} alt="" loading="lazy" />
}

/**
 * Horizontal row of 16x9 content cards rendered below the stitched player. Used for both
 * "Marketplace Products" (when `kind` is `product`) and "Articles" (when `kind` is `article`).
 * Cards open in a new tab and pause the stitched player on click so audio doesn't play in two
 * places at once.
 */
function MosaicContentRow ({
  items, label, kind, descriptionField, onCardClick,
}) {
  if (!items || items.length === 0) {
    return null
  }
  return (
    <section className="semantic-stitch-page__mosaic-row" aria-label={label}>
      <H3 className="semantic-stitch-page__mosaic-row-header">{label}</H3>
      <ul className="semantic-stitch-page__mosaic-row-list">
        {items.map((item) => {
          const href = buildContentSectionHref(item, kind)
          if (!href) {
            return null
          }
          const productThumbnailUrl = kind === 'product' ? mosaicProductThumbnailUrl(item) : ''
          const description = item[descriptionField] || ''
          return (
            <li key={`${kind}-${item.id}`} className="semantic-stitch-page__mosaic-card-item">
              <Link
                className="semantic-stitch-page__mosaic-card"
                to={href}
                target={TARGET_BLANK}
                onClick={onCardClick}
                directLink
              >
                <div
                  className={[
                    'semantic-stitch-page__mosaic-card-thumb',
                    kind === 'product' && 'semantic-stitch-page__mosaic-card-thumb--4x3',
                  ].filter(Boolean).join(' ')}
                >
                  {kind === 'article' && (
                    <MosaicArticleStockThumbnail />
                  )}
                  {kind === 'product' && (
                    productThumbnailUrl
                      ? <img src={productThumbnailUrl} alt="" loading="lazy" />
                      : <div className="semantic-stitch-page__mosaic-card-thumb--placeholder" />
                  )}
                </div>
                <div className="semantic-stitch-page__mosaic-card-text">
                  <span className="semantic-stitch-page__mosaic-card-title">{item.title || ''}</span>
                  {description && (
                    <span className="semantic-stitch-page__mosaic-card-desc">{description}</span>
                  )}
                </div>
              </Link>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

MosaicContentRow.propTypes = {
  items: PropTypes.array.isRequired,
  label: PropTypes.string.isRequired,
  kind: PropTypes.oneOf(['product', 'article']).isRequired,
  descriptionField: PropTypes.string.isRequired,
  onCardClick: PropTypes.func,
}

MosaicContentRow.defaultProps = {
  onCardClick: undefined,
}

/**
 * Collapsible Advanced Filters tray.
 * Renders:
 *   - Scope radio group (Any content / A video / A series / A topic / A center)
 *   - Typeahead input when scope ≠ ANY
 *   - Season dropdown when scope === SERIES
 *   - Published age dropdown
 *   - Active filter summary badge
 */
function AdvancedFilters ({
  filterScope, setFilterScope,
  filterItem, setFilterItem,
  filterSeason, setFilterSeason,
  filterPublishedAge, setFilterPublishedAge,
  lang,
  auth,
  staticText,
}) {
  const t = (key) => staticText.getIn(['data', key], '')
  const client = useApolloClient()

  const [open, setOpen] = useState(false)
  const [typeaheadTerm, setTypeaheadTerm] = useState('')
  const [typeaheadOptions, setTypeaheadOptions] = useState([])
  const [typeaheadLoading, setTypeaheadLoading] = useState(false)

  const scopeRequiresItem = filterScope !== FILTER_SCOPE_ANY

  // Determine a language param that includes the primary marker for contentList typeahead.
  // E.g. 'en' becomes ['en', '_primary'].
  const bcp47 = String(lang || 'EN').toLowerCase()
  const typeaheadLanguage = useMemo(() => {
    const code = bcp47.split('-')[0]
    return [code, '_primary']
  }, [bcp47])

  const executeTypeahead = useCallback(
    debounce(async (scope, term) => {
      const where = buildTypeaheadWhere(scope, term)
      if (!where) return
      setTypeaheadLoading(true)
      try {
        const jwt = getAuthJwt(auth)
        const { data } = await client.query({
          query: GET_CONTENT_SEARCH_OPTIONS,
          variables: { where, language: typeaheadLanguage, limit: TYPEAHEAD_LIMIT },
          fetchPolicy: 'network-only',
          context: jwt ? { headers: { authorization: `Bearer ${jwt}` } } : {},
        })
        setTypeaheadOptions(data?.contentList?.content ?? [])
      } catch {
        setTypeaheadOptions([])
      } finally {
        setTypeaheadLoading(false)
      }
    }, TYPEAHEAD_DEBOUNCE_MS),
    [client, auth, typeaheadLanguage],
  )

  // Prefetch when scope changes to a non-ANY value
  useEffect(() => {
    if (scopeRequiresItem) {
      setTypeaheadTerm('')
      setFilterItem(null)
      setTypeaheadOptions([])
      executeTypeahead(filterScope, '')
    }
  }, [filterScope, scopeRequiresItem, setFilterItem, executeTypeahead])

  useEffect(() => () => {
    executeTypeahead.cancel()
  }, [executeTypeahead])

  const handleScopeChange = useCallback((scope) => {
    setFilterScope(scope)
    setFilterItem(null)
    setFilterSeason(null)
    setTypeaheadTerm('')
    setTypeaheadOptions([])
  }, [setFilterScope, setFilterItem, setFilterSeason])

  const handleTypeaheadChange = useCallback((e) => {
    const { value } = e.target
    setTypeaheadTerm(value)
    setFilterItem(null)
    executeTypeahead(filterScope, value)
  }, [executeTypeahead, filterScope, setFilterItem])

  const handleSelectItem = useCallback((item) => {
    setFilterItem(item)
    setTypeaheadTerm(item.title || '')
    setTypeaheadOptions([])
  }, [setFilterItem])

  const handleClear = useCallback(() => {
    setFilterScope(FILTER_SCOPE_ANY)
    setFilterItem(null)
    setFilterSeason(null)
    setFilterPublishedAge('ANY')
    setTypeaheadTerm('')
    setTypeaheadOptions([])
  }, [setFilterScope, setFilterItem, setFilterSeason, setFilterPublishedAge])

  const hasActiveFilter = hasActiveFilterState({
    filterScope, filterItem, filterPublishedAge,
  })
  const filterScopeIncomplete = isFilterScopeIncomplete({ filterScope, filterItem })

  const activeFilterSummaryParts = useMemo(() => {
    const parts = []
    if (filterScope !== FILTER_SCOPE_ANY && filterItem) {
      parts.push(filterItem.title || '')
    }
    if (filterSeason != null) {
      parts.push(`S${filterSeason}`)
    }
    if (filterPublishedAge !== 'ANY') {
      const opt = PUBLISHED_AGE_OPTIONS.find((o) => o.value === filterPublishedAge)
      if (opt) parts.push(t(opt.labelKey))
    }
    return parts
  }, [filterScope, filterItem, filterSeason, filterPublishedAge, t])

  return (
    <div className="semantic-stitch-page__filters">
      <button
        type="button"
        className={[
          'semantic-stitch-page__filters-toggle',
          open && 'semantic-stitch-page__filters-toggle--open',
          hasActiveFilter && 'semantic-stitch-page__filters-toggle--active',
        ].filter(Boolean).join(' ')}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {t('filterLabel')}
        {hasActiveFilter && (
          <span className="semantic-stitch-page__filters-badge">
            {activeFilterSummaryParts.join(' · ')}
          </span>
        )}
      </button>

      {open && (
        <div className="semantic-stitch-page__filters-body">
          {/* Scope row */}
          <div className="semantic-stitch-page__filter-row">
            <span className="semantic-stitch-page__filter-label" id="ss-filter-scope-label">
              {t('filterScopeLabel')}
            </span>
            <div
              className="semantic-stitch-page__filter-scope-group"
              role="radiogroup"
              aria-labelledby="ss-filter-scope-label"
            >
              {[
                FILTER_SCOPE_ANY,
                FILTER_SCOPE_SERIES,
                FILTER_SCOPE_TOPIC,
                FILTER_SCOPE_CENTER,
                FILTER_SCOPE_VIDEO,
              ].map((scopeVal) => (
                <label
                  key={scopeVal}
                  className={[
                    'semantic-stitch-page__filter-scope-option',
                    filterScope === scopeVal && 'semantic-stitch-page__filter-scope-option--active',
                  ].filter(Boolean).join(' ')}
                >
                  <input
                    type="radio"
                    name="ss-filter-scope"
                    value={scopeVal}
                    checked={filterScope === scopeVal}
                    onChange={() => handleScopeChange(scopeVal)}
                    className="semantic-stitch-page__pill-input"
                  />
                  {t(FILTER_SCOPE_LABEL_KEYS[scopeVal])}
                </label>
              ))}
            </div>
          </div>

          {/* Typeahead row — visible when scope ≠ ANY */}
          {scopeRequiresItem && (
            <div className="semantic-stitch-page__filter-row">
              <div className="semantic-stitch-page__filter-typeahead">
                <input
                  type="text"
                  className="semantic-stitch-page__filter-typeahead-input"
                  value={typeaheadTerm}
                  onChange={handleTypeaheadChange}
                  placeholder={t('filterTypeaheadPlaceholder')}
                  aria-label={t(FILTER_SCOPE_LABEL_KEYS[filterScope])}
                  autoComplete="off"
                />
                {typeaheadLoading && (
                  <span className="semantic-stitch-page__filter-typeahead-loading" />
                )}
                {typeaheadOptions.length > 0 && (
                  <ul className="semantic-stitch-page__filter-typeahead-options" role="listbox">
                    {typeaheadOptions.map((item) => (
                      <li
                        key={item.contentId}
                        className="semantic-stitch-page__filter-typeahead-option"
                        role="option"
                        aria-selected={filterItem?.contentId === item.contentId}
                        onClick={() => handleSelectItem(item)}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleSelectItem(item)}
                        tabIndex={0}
                      >
                        {item.title}
                      </li>
                    ))}
                  </ul>
                )}
                {filterScopeIncomplete && (
                  <p className="semantic-stitch-page__filter-scope-hint" role="status">
                    {t('filterScopeRequired')}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Season dropdown — only when scope === SERIES */}
          {filterScope === FILTER_SCOPE_SERIES && (
            <div className="semantic-stitch-page__filter-row">
              <label
                className="semantic-stitch-page__filter-label"
                htmlFor="ss-filter-season"
              >
                {t('filterSeasonLabel')}
              </label>
              <select
                id="ss-filter-season"
                className="semantic-stitch-page__filter-select"
                value={filterSeason ?? ''}
                onChange={(e) => setFilterSeason(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">{t('filterSeasonAny')}</option>
                {Array.from({ length: MAX_SEASONS }, (_, i) => i + 1).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          )}

          {/* Published age row */}
          <div className="semantic-stitch-page__filter-row">
            <label
              className="semantic-stitch-page__filter-label"
              htmlFor="ss-filter-published-age"
            >
              {t('filterPublishedAgeLabel')}
            </label>
            <select
              id="ss-filter-published-age"
              className="semantic-stitch-page__filter-select"
              value={filterPublishedAge}
              onChange={(e) => setFilterPublishedAge(e.target.value)}
            >
              {PUBLISHED_AGE_OPTIONS.map(({ value, labelKey }) => (
                <option key={value} value={value}>{t(labelKey)}</option>
              ))}
            </select>
          </div>

          {/* Clear button */}
          {hasActiveFilter && (
            <div className="semantic-stitch-page__filter-row">
              <button
                type="button"
                className="semantic-stitch-page__filter-clear"
                onClick={handleClear}
              >
                {t('filterClear')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

AdvancedFilters.propTypes = {
  filterScope: PropTypes.string.isRequired,
  setFilterScope: PropTypes.func.isRequired,
  filterItem: PropTypes.object,
  setFilterItem: PropTypes.func.isRequired,
  filterSeason: PropTypes.number,
  setFilterSeason: PropTypes.func.isRequired,
  filterPublishedAge: PropTypes.string.isRequired,
  setFilterPublishedAge: PropTypes.func.isRequired,
  lang: PropTypes.string.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
}

AdvancedFilters.defaultProps = {
  filterItem: null,
  filterSeason: null,
}

function SemanticStitchPage (props) {
  const { location, staticText } = props
  const client = useApolloClient()
  const auth = useSelector((state) => state.auth)
  const user = useSelector((state) => state.user)
  const touchable = useSelector((state) => (
    ImMap.isMap(state.app)
      ? state.app.getIn(['viewport', 'touchable'], false)
      : false
  ))
  const [prompt, setPrompt] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [curationMode, setCurationMode] = useState(DEFAULT_CURATION_MODE)
  const [lastSubmittedCurationMode, setLastSubmittedCurationMode] = useState(null)

  // Advanced filter state
  const [filterScope, setFilterScope] = useState(FILTER_SCOPE_ANY)
  const [filterItem, setFilterItem] = useState(null)
  const [filterSeason, setFilterSeason] = useState(null)
  const [filterPublishedAge, setFilterPublishedAge] = useState('ANY')
  const [currentTime, setCurrentTime] = useState(0)
  const [playerReady, setPlayerReady] = useState(false)
  const [isCompact, setIsCompact] = useState(false)
  const [manifestClipDurations, setManifestClipDurations] = useState(null)
  const [exampleIdx, setExampleIdx] = useState(0)
  const [showTransitions, setShowTransitions] = useState(false)

  const lang = useMemo(
    () => primaryLanguageToSemanticSearchLanguage(
      getPrimary(user.getIn(['data', 'language'], List())),
    ),
    [user],
  )

  const searchExamples = useMemo(() => {
    const raw = staticText.getIn(['data', 'searchExamples'])
    if (List.isList(raw)) return raw.toArray()
    return []
  }, [staticText])

  const filterScopeIncomplete = isFilterScopeIncomplete({ filterScope, filterItem })

  const handlePlaceholderIteration = useCallback(() => {
    setExampleIdx((i) => (i + 1) % Math.max(searchExamples.length, 1))
  }, [searchExamples.length])

  const playerRef = useRef(null)
  const resultsRef = useRef(null)
  const searchSectionRef = useRef(null)
  const curationModeRef = useRef(null)
  const st = useCallback(
    (key) => staticText.getIn(['data', key], ''),
    [staticText],
  )

  useEffect(() => {
    if (data && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [data])

  const handlePlayerReady = useCallback((player) => {
    playerRef.current = player
    setPlayerReady(true)
  }, [])

  const handlePlayerError = useCallback((err) => {
    setError((err && err.message) ? err.message : st('playerLoadError'))
  }, [st])

  // Pause the stitched player when a marketplace product or article card is opened in a new tab,
  // so audio doesn't play in two places at once. Mirrors the existing `handleContinue` pattern.
  const handleContentCardClick = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.pause()
    }
  }, [])

  useEffect(() => {
    if (!playerReady || !playerRef.current) return undefined
    const p = playerRef.current
    const handler = () => setCurrentTime(p.currentTime())
    p.on('timeupdate', handler)
    return () => p.off('timeupdate', handler)
  }, [playerReady])

  /**
   * The stitcher places #EXT-X-DISCONTINUITY between clips. Segments are selected by
   * overlap — so each clip in the manifest starts slightly BEFORE the startTimecode and
   * ends slightly AFTER the endTimecode (HLS segment-boundary alignment). The cumulative
   * sum of logical clip durations (endSec - startSec) therefore under-counts the real
   * stitched timeline, causing the sidebar to switch clips early and "Continue" links to
   * jump ahead. Parsing the manifest gives us the true per-clip durations.
   */
  const manifestUrl = data?.stitchedPlayback?.manifestUrl
  useEffect(() => {
    if (!manifestUrl) {
      setManifestClipDurations(null)
      return undefined
    }
    let cancelled = false

    /**
     * Return the media playlist text to parse. When manifestUrl is a master playlist
     * (Brightcove demuxed-audio content: contains #EXT-X-STREAM-INF) the route also
     * serves ?track=video which is the real video media playlist with #EXTINF lines.
     * For flat/muxed content the manifest IS the media playlist already.
     */
    const resolveMediaPlaylist = (url) => {
      return fetch(url)
        .then((r) => r.text())
        .then((text) => {
          const subPlaylistUrl = resolveStreamInfMediaUri(text)
          if (!subPlaylistUrl) return text
          return fetch(new URL(subPlaylistUrl, url).href).then((r) => r.text())
        })
    }

    resolveMediaPlaylist(manifestUrl)
      .then((text) => {
        if (cancelled) return
        const lines = text.split(/\r?\n/u)
        const durations = []
        let clipDur = 0
        let hasSegs = false
        lines.forEach((line) => {
          const t = line.trim()
          if (t === '#EXT-X-DISCONTINUITY') {
            // Boundary between clips — commit the duration for the clip that just ended.
            durations.push(clipDur)
            clipDur = 0
            hasSegs = false
          } else if (t.startsWith('#EXTINF')) {
            const m = t.match(/#EXTINF:([0-9.]+)/u)
            if (m) {
              clipDur += parseFloat(m[1])
              hasSegs = true
            }
          }
        })
        if (hasSegs || clipDur > 0) {
          durations.push(clipDur) // commit the final clip
        }
        setManifestClipDurations(durations)
      })
      .catch(() => {
        // Silently fall back to timecode-computed durations.
      })
    return () => {
      cancelled = true
    }
  }, [manifestUrl])

  const handleChangeStyle = useCallback(() => {
    if (searchSectionRef.current) {
      searchSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    const firstPresetInput = curationModeRef.current?.querySelector('input[type="radio"]')
    if (firstPresetInput) {
      firstPresetInput.focus()
    }
  }, [])

  const onSubmit = useCallback(async (e) => {
    e.preventDefault()
    setError(null)
    setData(null)
    setCurrentTime(0)
    setPlayerReady(false)
    setManifestClipDurations(null)
    playerRef.current = null
    setLoading(true)
    try {
      const jwt = getAuthJwt(auth)
      if (!jwt) {
        setError(st('signInRequired'))
        return
      }
      const activeFilter = buildSearchFilter({
        filterScope, filterItem, filterSeason, filterPublishedAge,
      })
      const { data: res, errors } = await client.query({
        query: DOCUMENT_SEMANTIC_SEARCH,
        variables: {
          input: {
            prompt: prompt.trim(),
            language: lang,
            maxResults: 10,
            scope: 'TRANSCRIPT_TIMED',
            curationMode,
            includeTransitions: MOSAIC_SEARCH_INCLUDE_TRANSITIONS,
            filter: activeFilter,
          },
          includeSummary: MOSAIC_SEARCH_INCLUDE_SUMMARY,
          includeProducts: true,
          includeArticles: true,
        },
        fetchPolicy: 'network-only',
        context: { headers: { authorization: `Bearer ${jwt}` } },
      })
      if (errors && errors.length) {
        setError(errors.map((er) => er.message).join('; '))
        return
      }
      setData(res?.documentSemanticSearch)
      setLastSubmittedCurationMode(curationMode)
    } catch (err) {
      setError(err && err.message ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [client, auth, prompt, lang, curationMode, st,
    filterScope, filterItem, filterSeason, filterPublishedAge])

  const playback = data && data.stitchedPlayback
  const reason = playback && playback.unavailableReason
  const subtitlesUrl = playback?.subtitlesUrl ?? null

  const hasSpans = Boolean(data?.spans?.length)
  const playbackBlocked = data && !manifestUrl && reason && reason !== 'READY'
  // When the search returned zero spans, treat as "no semantic results" regardless of which
  // unavailableReason the stitcher returned. Otherwise (spans exist but no manifest), the
  // search succeeded but stitching failed — surface that as a tech hiccup instead.
  const showNoResults = playbackBlocked && !hasSpans
  const showPlaybackError = playbackBlocked && hasSpans

  // Build allSpans: prefer stitchedPlayback.spans ordering (includes TRANSITION kind),
  // merging in the rich display data from data.spans for CONTENT entries so ClipCards
  // continue to show transcript, description, season/episode info, etc.
  const allSpans = useMemo(() => {
    const stitchedSpans = data?.stitchedPlayback?.spans
    const contentSpans = data?.spans ?? []
    if (!Array.isArray(stitchedSpans) || stitchedSpans.length === 0) {
      return contentSpans
    }
    const spanMap = new Map(
      contentSpans.map((s) => [`${s.contentId}-${s.startTimecode}`, s]),
    )
    return stitchedSpans.map((span) => {
      if (span.kind === 'TRANSITION') return span
      const richSpan = spanMap.get(`${span.contentId}-${span.startTimecode}`)
      return richSpan ? { ...richSpan, kind: span.kind } : span
    })
  }, [data])

  const clips = useMemo(() => {
    const base = buildClipOffsets(allSpans)
    // Replace computed (timecode-only) durations with manifest-derived actual durations.
    // The HLS stitcher selects segments by overlap, so each clip in the stitched manifest
    // starts slightly before startTimecode and ends slightly after endTimecode. Using the
    // real #EXTINF sums ensures the sidebar switches clips at the correct moment and
    // "Continue watching" links map to the right source position.
    if (manifestClipDurations && manifestClipDurations.length === base.length) {
      let acc = 0
      return base.map((clip, i) => {
        const stitchedStart = acc
        const clipDuration = manifestClipDurations[i]
        acc += clipDuration
        return { ...clip, stitchedStart, clipDuration }
      })
    }
    return base
  }, [allSpans, manifestClipDurations])

  // Content-only clips used for sidebar default view and chapter markers.
  const contentClips = useMemo(
    () => clips.filter((c) => !c.kind || c.kind === 'CONTENT'),
    [clips],
  )

  const hasTransitions = useMemo(
    () => clips.some((c) => c.kind === 'TRANSITION'),
    [clips],
  )

  const activeIndex = useMemo(
    () => clips.findIndex(
      (c) => currentTime >= c.stitchedStart && currentTime < c.stitchedStart + c.clipDuration,
    ),
    [clips, currentTime],
  )

  // Place seek-bar chapter markers derived from the manifest-corrected clip boundaries.
  // Markers are placed only at content clip boundaries — transitions are intentionally
  // excluded so the seek bar shows the content structure, not every clip segment.
  // Re-runs whenever contentClips updates (i.e. when manifestClipDurations arrives and
  // corrects the timecode-only estimates).
  useEffect(() => {
    if (!playerReady || !playerRef.current || contentClips.length < 2) return undefined

    const player = playerRef.current

    function placeMarkers () {
      const duration = player.duration()
      if (!duration) return

      const seekBarEl = player.controlBar
        ?.getChild('ProgressControl')
        ?.getChild('SeekBar')
        ?.el()
      if (!seekBarEl) return

      seekBarEl.querySelectorAll('.vjs-chapter-marker').forEach((m) => m.remove())

      contentClips.forEach((clip, i) => {
        if (i === 0) return // first clip starts at t=0; no boundary marker needed
        const { stitchedStart } = clip
        if (stitchedStart <= 0 || stitchedStart >= duration) return
        const marker = document.createElement('div')
        marker.className = 'vjs-chapter-marker'
        marker.style.cssText = 'position:absolute;top:0;bottom:0;width:2px;background:rgba(255,255,255,0.6);z-index:10;pointer-events:none;'
        marker.style.left = `${(stitchedStart / duration) * 100}%`
        marker.title = clip.content?.title || `Clip ${i + 1}`
        seekBarEl.appendChild(marker)
      })
    }

    if (player.readyState() >= 1) {
      placeMarkers()
    } else {
      player.one('loadedmetadata', placeMarkers)
      return () => player.off('loadedmetadata', placeMarkers)
    }
    return undefined
  }, [contentClips, playerReady])

  return (
    <div className="page semantic-stitch-page" data-location-pathname={location && location.pathname}>

      {/* ── Search section (always visible) ─────────────────────────────────── */}
      <div ref={searchSectionRef} className="semantic-stitch-page__search-section">
        <div className="semantic-stitch-page__search-inner">
          {!data && (
            <h1 className="semantic-stitch-page__page-title">{st('pageTitle')}</h1>
          )}
          <p className="semantic-stitch-page__search-heading">{st('searchLabel')}</p>
          <form className="semantic-stitch-page__search-form" onSubmit={onSubmit}>
            <div className="semantic-stitch-page__search-bar">
              <span className="semantic-stitch-page__search-icon">
                <GaiaLeaf size={18} color="#01b4b4" />
              </span>
              <div className="semantic-stitch-page__search-input-wrap">
                <input
                  id="ss-prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="semantic-stitch-page__search-input"
                  autoComplete="off"
                  aria-label={st('promptLabel')}
                />
                {!prompt && searchExamples.length > 0 && (
                  <span
                    className="semantic-stitch-page__search-example"
                    onAnimationIteration={handlePlaceholderIteration}
                    aria-hidden="true"
                  >
                    {searchExamples[exampleIdx]}
                  </span>
                )}
              </div>
              <button
                type="submit"
                className={[
                  'semantic-stitch-page__search-submit',
                  (loading || !prompt.trim() || filterScopeIncomplete) && 'semantic-stitch-page__search-submit--disabled',
                ].filter(Boolean).join(' ')}
                disabled={loading || !prompt.trim() || filterScopeIncomplete}
                aria-label={st('revealMosaic')}
              >
                {loading
                  ? <span className="semantic-stitch-page__search-spinner" />
                  : <ArrowRight size={18} color="#fff" />}
              </button>
            </div>
            <div className="semantic-stitch-page__curation-mode">
              <span id="ss-curation-mode-label" className="semantic-stitch-page__curation-mode-label">
                {st('curationModeLabel')}
              </span>
              <div
                ref={curationModeRef}
                className="semantic-stitch-page__curation-mode-grid"
                role="radiogroup"
                aria-labelledby="ss-curation-mode-label"
              >
                {CURATION_MODE_PRESETS.map((presetId) => (
                  <label
                    key={presetId}
                    className={[
                      'semantic-stitch-page__curation-mode-card',
                      curationMode === presetId && 'semantic-stitch-page__curation-mode-card--active',
                    ].filter(Boolean).join(' ')}
                  >
                    <input
                      type="radio"
                      name="ss-curation-mode"
                      value={presetId}
                      checked={curationMode === presetId}
                      onChange={() => setCurationMode(presetId)}
                      className="semantic-stitch-page__pill-input"
                    />
                    <span className="semantic-stitch-page__curation-mode-card-title">
                      {st(CURATION_MODE_LABEL_KEY[presetId])}
                    </span>
                    <span className="semantic-stitch-page__curation-mode-card-desc">
                      {st(CURATION_MODE_DESC_KEY[presetId])}
                    </span>
                  </label>
                ))}
              </div>
              <p className="semantic-stitch-page__curation-mode-hint">{st('curationModeHint')}</p>
            </div>
            <AdvancedFilters
              filterScope={filterScope}
              setFilterScope={setFilterScope}
              filterItem={filterItem}
              setFilterItem={setFilterItem}
              filterSeason={filterSeason}
              setFilterSeason={setFilterSeason}
              filterPublishedAge={filterPublishedAge}
              setFilterPublishedAge={setFilterPublishedAge}
              lang={lang}
              auth={auth}
              staticText={staticText}
            />
          </form>
          {error && (
            <p className="semantic-stitch-page__error" role="alert">{error}</p>
          )}
        </div>
      </div>

      {/* ── Pre-search: feature highlights ──────────────────────────────────── */}
      {!data && !loading && (
        <div className="semantic-stitch-page__features">
          <div className="semantic-stitch-page__feature">
            <span className="semantic-stitch-page__feature-icon">
              <Reply size={36} color="#01b4b4" />
            </span>
            <span className="semantic-stitch-page__feature-text">{st('featureAsk')}</span>
          </div>
          <div className="semantic-stitch-page__feature">
            <span className="semantic-stitch-page__feature-icon">
              <Metaphysics size={36} color="#01b4b4" />
            </span>
            <span className="semantic-stitch-page__feature-text">{st('featureAI')}</span>
          </div>
          <div className="semantic-stitch-page__feature">
            <span className="semantic-stitch-page__feature-icon">
              <PlayPlaylistOutline size={36} color="#01b4b4" />
            </span>
            <span className="semantic-stitch-page__feature-text">{st('featureStitch')}</span>
          </div>
        </div>
      )}

      {/* ── Loading splash ───────────────────────────────────────────────────── */}
      {loading && (
        <MosaicLoadingSplash staticText={staticText} />
      )}

      {/* ── Post-search: mosaic header + player ─────────────────────────────── */}
      {data && (
        <div ref={resultsRef} className="semantic-stitch-page__results">
          <div className="semantic-stitch-page__mosaic-header">
            <span className="semantic-stitch-page__mosaic-label">{st('mosaicLabel')}</span>
            <span className="semantic-stitch-page__mosaic-sublabel">{st('mosaicSubLabel')}</span>
            {lastSubmittedCurationMode && (
              <div className="semantic-stitch-page__curated-as">
                <span className="semantic-stitch-page__curated-as-label">
                  {st('curatedAsLabel')}
                  {' '}
                  {st(CURATION_MODE_LABEL_KEY[lastSubmittedCurationMode])}
                </span>
                <button
                  type="button"
                  className="semantic-stitch-page__change-style-link"
                  onClick={handleChangeStyle}
                >
                  {st('changeStyleLabel')}
                </button>
              </div>
            )}
          </div>
          {manifestUrl && (
            <div className="semantic-stitch-page__player-section">
              <StitchedManifestPlayer
                manifestUrl={manifestUrl}
                subtitlesUrl={subtitlesUrl}
                language={lang}
                onPlayerReady={handlePlayerReady}
                onPlayerError={handlePlayerError}
                user={user}
                staticText={staticText}
                touchable={touchable}
              />
              {(contentClips.length > 0 || hasTransitions) && (
                <ClipSidebar
                  clips={clips}
                  contentClips={contentClips}
                  activeIndex={activeIndex}
                  showTransitions={showTransitions}
                  hasTransitions={hasTransitions}
                  onToggleShowTransitions={() => setShowTransitions((v) => !v)}
                  currentTime={currentTime}
                  playerRef={playerRef}
                  isCompact={isCompact}
                  onToggleCompact={() => setIsCompact((v) => !v)}
                  staticText={staticText}
                />
              )}
            </div>
          )}
          {showNoResults && (
            <div className="semantic-stitch-page__empty-state" role="status">
              <p className="semantic-stitch-page__empty-state-heading">{st('noResultsHeading')}</p>
              <p className="semantic-stitch-page__empty-state-body">{st('noResultsBody')}</p>
            </div>
          )}
          {showPlaybackError && (
            <div className="semantic-stitch-page__empty-state semantic-stitch-page__empty-state--error" role="status">
              <p className="semantic-stitch-page__empty-state-heading">{st('playbackErrorHeading')}</p>
              <p className="semantic-stitch-page__empty-state-body">{st('playbackErrorBody')}</p>
            </div>
          )}
          {data.stitchedPlayback?.stitchedWithPartialContent && (
            <p className="semantic-stitch-page__result-meta">{st('partialLabel')}</p>
          )}
          {data.summary && (
            <section className="semantic-stitch-page__summary" aria-label={st('summarySectionHeading')}>
              <H3 className="semantic-stitch-page__summary-heading">{st('summarySectionHeading')}</H3>
              <p className="semantic-stitch-page__summary-text">{data.summary}</p>
            </section>
          )}
          {data.products?.length > 0 && (
            <MosaicContentRow
              items={data.products}
              label={st('productsSectionLabel')}
              kind="product"
              descriptionField="shortDescription"
              onCardClick={handleContentCardClick}
            />
          )}
          {data.articles?.length > 0 && (
            <MosaicContentRow
              items={data.articles}
              label={st('articlesSectionLabel')}
              kind="article"
              descriptionField="seoDescription"
              onCardClick={handleContentCardClick}
            />
          )}
        </div>
      )}

    </div>
  )
}

SemanticStitchPage.propTypes = {
  location: PropTypes.object.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
}

export default compose(
  connectStaticText({ storeKey: 'semanticStitchPage' }),
)(SemanticStitchPage)
